import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import Seo from "../../components/Seo";
import { ShieldCheck } from "lucide-react";

const inputCls =
  "w-full border border-sage bg-white px-4 py-3 text-sm text-charcoal focus:border-forest focus:ring-1 focus:ring-forest rounded-sm";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("password"); // password | magic | mfa_setup | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [preToken, setPreToken] = useState("");
  const [totpSetup, setTotpSetup] = useState(null);
  const [state, setState] = useState({ status: "idle", message: "" });

  const afterAuth = () => navigate("/admin", { replace: true });

  const submitPassword = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_setup_required) {
        setPreToken(data.pre_token);
        const setup = await api.post("/auth/mfa/setup", { pre_token: data.pre_token });
        setTotpSetup(setup.data);
        setMode("mfa_setup");
      } else if (data.mfa_required) {
        setPreToken(data.pre_token);
        setMode("mfa");
      }
      setState({ status: "idle", message: "" });
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const endpoint = mode === "mfa_setup" ? "/auth/mfa/enable" : "/auth/mfa/verify";
      await api.post(endpoint, { pre_token: preToken, code });
      afterAuth();
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  const submitMagic = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/auth/magic/request", { email });
      setState({ status: "success", message: data.message });
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-6 py-16">
      <Seo title="Administrator Sign In" path="/admin/login" description="Private administrative access for Mount Moriah Relief." />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" data-testid="admin-login-home-link">
            <img src="/logo-full.png" alt="Mount Moriah Relief logo" className="h-28 w-auto mx-auto" />
          </Link>
          <p className="mt-3 text-xs tracking-[0.25em] uppercase font-semibold text-charcoal/50">
            Administration — authorised users only
          </p>
        </div>

        <div className="bg-white border border-sandstone p-8" data-testid="admin-login-card">
          {mode === "password" && (
            <form onSubmit={submitPassword} className="space-y-5">
              <h1 className="font-serif text-2xl font-semibold text-forest">Sign in</h1>
              <div>
                <label htmlFor="admin-email" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Email address</label>
                <input id="admin-email" type="email" required autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" />
              </div>
              <div>
                <label htmlFor="admin-password" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Password</label>
                <input id="admin-password" type="password" required autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" />
              </div>
              <button type="submit" disabled={state.status === "loading"} data-testid="admin-login-submit"
                className="w-full bg-forest text-white font-medium px-6 py-3 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
                {state.status === "loading" ? "Checking…" : "Continue"}
              </button>
              <button type="button" onClick={() => { setMode("magic"); setState({ status: "idle", message: "" }); }}
                data-testid="admin-magic-switch"
                className="w-full text-sm text-forest hover:underline">
                Email me a sign-in link instead
              </button>
            </form>
          )}

          {mode === "magic" && (
            <form onSubmit={submitMagic} className="space-y-5">
              <h1 className="font-serif text-2xl font-semibold text-forest">Sign-in link</h1>
              <p className="text-sm text-charcoal/70 leading-relaxed">
                Invited administrators sign in with a single-use link sent to their email address.
              </p>
              <div>
                <label htmlFor="magic-email" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Email address</label>
                <input id="magic-email" type="email" required autoComplete="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="magic-email-input" />
              </div>
              {state.status === "success" ? (
                <p className="text-sm text-forest font-medium" data-testid="magic-sent-message">{state.message}</p>
              ) : (
                <button type="submit" disabled={state.status === "loading"} data-testid="magic-request-submit"
                  className="w-full bg-forest text-white font-medium px-6 py-3 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
                  {state.status === "loading" ? "Sending…" : "Send sign-in link"}
                </button>
              )}
              <button type="button" onClick={() => { setMode("password"); setState({ status: "idle", message: "" }); }}
                className="w-full text-sm text-forest hover:underline" data-testid="password-switch">
                Sign in with password
              </button>
            </form>
          )}

          {(mode === "mfa" || mode === "mfa_setup") && (
            <form onSubmit={submitCode} className="space-y-5">
              <h1 className="font-serif text-2xl font-semibold text-forest">
                {mode === "mfa_setup" ? "Set up two-factor authentication" : "Two-factor authentication"}
              </h1>
              {mode === "mfa_setup" && totpSetup && (
                <div className="bg-sandstone/50 border border-sandstone p-5 space-y-3" data-testid="totp-setup-block">
                  <p className="text-sm text-charcoal/80 leading-relaxed">
                    Add this account to your authenticator app (such as Google Authenticator or
                    Authy) using the setup key below, then enter the 6-digit code it shows.
                  </p>
                  <p className="text-xs tracking-wider uppercase font-semibold text-charcoal/60">Setup key</p>
                  <code className="block break-all bg-white border border-sandstone px-3 py-2 text-sm text-forest" data-testid="totp-secret">
                    {totpSetup.secret}
                  </code>
                  <p className="text-xs text-charcoal/60 break-all">{totpSetup.otpauth_uri}</p>
                </div>
              )}
              {mode === "mfa" && (
                <p className="text-sm text-charcoal/70 leading-relaxed">
                  Enter the 6-digit code from your authenticator app.
                </p>
              )}
              <div>
                <label htmlFor="totp-code" className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2">Authentication code</label>
                <input id="totp-code" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                  autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)}
                  className={`${inputCls} text-center text-2xl tracking-[0.5em] font-semibold`} data-testid="totp-code-input" />
              </div>
              <button type="submit" disabled={state.status === "loading"} data-testid="totp-submit"
                className="w-full bg-forest text-white font-medium px-6 py-3 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
                {state.status === "loading" ? "Verifying…" : "Verify & sign in"}
              </button>
            </form>
          )}

          {state.status === "error" && (
            <p className="mt-4 text-sm text-red-700" role="alert" data-testid="admin-login-error">{state.message}</p>
          )}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-charcoal/50">
          <ShieldCheck size={14} aria-hidden="true" />
          Protected by multifactor authentication, rate limiting and audit logging.
        </p>
      </div>
    </div>
  );
}
